import React from 'react';
import styled from 'styled-components';

/**
 * 뉴스레터 백오피스 공통 스타일
 * (피그마: 뉴스레터 관리 / 뉴스레터 등록)
 */

export const FieldGroup = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  gap: 8px;
`;

export const FieldLabel = styled.label`
  color: rgba(46, 47, 51, 0.88);
  font-size: 14px;
  font-weight: 600;
  line-height: 1.43;
  letter-spacing: 0.014em;
`;

export const FieldRow = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-end;
  gap: 16px;
`;

export const InlineRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 8px;
`;

export const TextInput = styled.input`
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 12px;
  padding: 0 16px;
  background: #fff;
  color: #171719;
  font-family: inherit;
  font-size: 16px;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }

  &:focus {
    border-color: #448181;
  }

  &:disabled {
    background: rgba(112, 115, 124, 0.05);
    cursor: not-allowed;
  }
`;

/** 여러 줄 입력용 — 엔터로 줄바꿈하면 사용자 페이지에서 그대로 줄이 나뉜다 (white-space: pre-line) */
export const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 12px;
  padding: 11px 16px;
  background: #fff;
  color: #171719;
  font-family: inherit;
  font-size: 16px;
  line-height: 1.5;
  outline: none;
  resize: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }

  &:focus {
    border-color: #448181;
  }

  &:disabled {
    background: rgba(112, 115, 124, 0.05);
    cursor: not-allowed;
  }
`;

/** 최대 줄 수를 넘는 줄바꿈은 잘라낸다 */
export const limitLines = (value: string, maxLines: number): string =>
  value.split('\n').slice(0, maxLines).join('\n');

/** 배너 썸네일 업로드 영역 (피그마 336 x 112, 3:1) */
export const BannerThumbnail = styled.button`
  display: flex;
  width: 336px;
  height: 112px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  padding: 0;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:hover {
    border-color: #448181;
  }
`;

export const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const IconButton = styled.button`
  display: flex;
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  padding: 0;
  background: #fff;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(68, 129, 129, 0.06);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
`;

export const ColorField = styled.div`
  display: flex;
  width: 132px;
  height: 48px;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 12px;
  padding: 0 12px;
  background: #fff;
`;

export const ColorSwatch = styled.input`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 6px;
  padding: 0;
  background: none;
  cursor: pointer;

  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }

  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

export const ColorText = styled.input`
  width: 100%;
  min-width: 0;
  border: 0;
  color: #448181;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  outline: none;
`;

/** "비율 21:9의 ... 업로드" 안내문 + [이미지 업로드] 버튼 줄 */
export const UploadRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

export const UploadCaption = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  line-height: 1.43;
`;

export const UploadButton = styled.button`
  display: flex;
  height: 48px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 12px;
  padding: 0 20px;
  background: #fff;
  color: #448181;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(68, 129, 129, 0.06);
  }
`;

/** 업로드된 이미지 미리보기 (aspect 비율은 화면마다 다름) */
export const ImagePreview = styled.div<{ $ratio: string }>`
  width: 100%;
  max-width: 480px;
  aspect-ratio: ${({ $ratio }) => $ratio};
  overflow: hidden;
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  background: rgba(112, 115, 124, 0.05);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ActionRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: 24px;
`;

export const TextButton = styled.button`
  border: 0;
  padding: 4px 16px;
  background: transparent;
  color: rgba(55, 56, 60, 0.61);
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    color: #171719;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const ErrorMessage = styled.div`
  color: #ec4e4e;
  font-size: 14px;
`;

export const PlusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.9994 4.13721C16.6622 4.13721 17.1994 4.67447 17.1994 5.33721V14.8039H26.6661C27.3288 14.8039 27.8661 15.3411 27.8661 16.0039C27.8661 16.6666 27.3288 17.2039 26.6661 17.2039H17.1994V26.6705C17.1994 27.3333 16.6622 27.8705 15.9994 27.8705C15.3367 27.8705 14.7994 27.3333 14.7994 26.6705V17.2039H5.33281C4.67007 17.2039 4.13281 16.6666 4.13281 16.0039C4.13281 15.3411 4.67007 14.8039 5.33281 14.8039H14.7994V5.33721C14.7994 4.67447 15.3367 4.13721 15.9994 4.13721Z"
      fill="#448181"
    />
  </svg>
);

export const MinusIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M3.09961 11.9995C3.09961 11.5025 3.50255 11.0995 3.99961 11.0995H19.9995C20.4966 11.0995 20.8995 11.5025 20.8995 11.9995C20.8995 12.4966 20.4966 12.8995 19.9995 12.8995H3.99961C3.50255 12.8995 3.09961 12.4966 3.09961 11.9995Z"
      fill="#448181"
    />
  </svg>
);

export const BannerPlusIcon: React.FC = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.9994 4.13725C16.6622 4.13725 17.1994 4.67451 17.1994 5.33725V14.8039H26.6661C27.3288 14.8039 27.8661 15.3412 27.8661 16.0039C27.8661 16.6667 27.3288 17.2039 26.6661 17.2039H17.1994V26.6706C17.1994 27.3333 16.6622 27.8706 15.9994 27.8706C15.3367 27.8706 14.7994 27.3333 14.7994 26.6706V17.2039H5.33281C4.67007 17.2039 4.13281 16.6667 4.13281 16.0039C4.13281 15.3412 4.67007 14.8039 5.33281 14.8039H14.7994V5.33725C14.7994 4.67451 15.3367 4.13725 15.9994 4.13725Z"
      fill="#448181"
    />
  </svg>
);
